import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { workflowActions, workflowSelectors } from 'state/workflow';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { authSelectors } from 'state/auth';
import { useEffect } from 'react';
import { progressBarActions } from 'state/progressBar';
import { ConfigurableHyperLinkSelector } from 'state/hyperlink/hyperlink.selectors';
import { Container } from 'react-bootstrap';
import WorkflowNavigationButton from 'components/Buttons/WorkflowNavigationButton/WorkflowNavigationButton';

interface ErrorComponentProps {
  disableReturn?: boolean;
  customError?: any;
  subTitle?: string;
  disableButton?: boolean;
  errorSubTitle?: string;
}
const DEFAULT_ERROR_HEADER = 'Something went wrong';
const DEFAULT_ERROR_TITLE = 'Please try again. If the problem persists contact support';
const DEFAULT_ERROR_BUTTON_TEXT = 'Retry';
const SUB_TITLE = 'Please return to the previous step and try to re-run';

const ErrorComponent = ({
  disableReturn,
  customError,
  disableButton = false,
  subTitle = SUB_TITLE,
  errorSubTitle,
}: ErrorComponentProps) => {
  const dispatch = useAppDispatch();

  const user = useAppSelector(authSelectors.userSelector);
  const error = useAppSelector(workflowSelectors.errorSelector);
  const lastStepActionName = useAppSelector(workflowSelectors.lastStepActionNameSelector);
  const hyperlinks = useAppSelector(ConfigurableHyperLinkSelector);

  const errorCode = !Array.isArray(error) && typeof error === 'object' && error?.code;

  useEffect(() => {
    dispatch(workflowActions.setLastStepBeforeErrorClose(lastStepActionName));
  }, []);

  const closeError = () => {
    dispatch(workflowActions.setError(false));
    if (lastStepActionName === 'submit_forms') {
      dispatch(progressBarActions.prevStep());
      dispatch(workflowActions.workflowPreviousStep());
      return;
    }

    dispatch(workflowActions.workflowRetryLatestActionStep());
  };

  const getErrorHeader = (): string => {
    if (customError) {
      return customError;
    }

    if (errorCode && !error.title && error.message) {
      return error.message;
    }

    if (errorCode && error.title) {
      if (errorCode === '0002' && lastStepActionName === 'unsigned_prescription') {
        return 'Something went wrong with cancelling the transaction';
      }
      return error.title;
    }
    return DEFAULT_ERROR_HEADER;
  };

  const getErrorTitle = (): string => {
    if (errorCode && error.sub_title) {
      if (errorCode === '0002' && lastStepActionName === 'unsigned_prescription') {
        return DEFAULT_ERROR_TITLE;
      }

      return error.sub_title;
    }
    if (errorSubTitle) {
      return errorSubTitle;
    }
    return subTitle;
  };

  const getErrorButtonText = (): string => {
    if (errorCode && error.button_text) {
      if (errorCode === '0002' && lastStepActionName === 'unsigned_prescription') {
        return DEFAULT_ERROR_BUTTON_TEXT;
      }

      return error.button_text;
    }

    return DEFAULT_ERROR_BUTTON_TEXT;
  };

  return (
    <>
      <div>
        <div className="g-0">
          {user && (
            <Container className="bg-light-gray border p-3">
              {user && <h5 className="m-0 fw-bold">{`Hello Dr. ${user.name}`}</h5>}
            </Container>
          )}
          <div className="d-flex pt-3 align-items-center">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="text-danger fa-2x me-3"
            />
            <h4 className="text-danger">{getErrorHeader()}</h4>
          </div>

          {!disableReturn && (
            <>
              {!disableButton && (
                <WorkflowNavigationButton
                  text={getErrorButtonText()}
                  variant="danger"
                  type="button"
                  className="me-3 mt-2"
                  onClick={closeError}
                />
              )}
              <Container className="bg-light-gray border p-3 mt-3">
                {((errorCode && error.sub_title) || errorSubTitle) && (
                  <h5 className="fw-bold">{getErrorTitle()}</h5>
                )}
                <div>
                  <div className="col-12 bg-light">
                    <div>
                      <a
                        id={`chat-with-us-${lastStepActionName}-${errorCode}`}
                        href={hyperlinks['support_chat_url']}
                        target="_blank"
                        className="text-purple "
                      >
                        Chat with us
                      </a>{' '}
                      or contact support at (855) 863-1355 and provide the following information:
                    </div>
                    {user && (
                      <div>
                        <div className="mt-2 fw-bold">ERROR INFORMATION</div>
                        <div className="mb-3"> {errorCode && error.message}</div>
                        <div className="d-flex">
                          <div>
                            <div>Practice Username: </div>
                            <div> Username: </div>
                            <div>NPI: </div>
                            <div>Email: </div>
                          </div>
                          <div className="ms-4 d-flex flex-column justify-content-end">
                            <div>{user.rc_practice_username} </div>
                            <div> {user.rc_username}</div>
                            <div> {user.npi}</div>
                            <div> {user.email}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Container>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ErrorComponent;
